import { ArrowLeftCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { setLoanGroupData } from 'actions'
import { fieldsByTransactionTypeAndProductTypeConstant, productTypeOptionsMap, ProductTypes } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { ILoan } from 'pages/LoanStructure/interfaces'
import {
  calculateLoanLimits,
  calculateLTV,
  estimatedClosingAmountBorrower,
  isNonDSCRProduct,
} from 'pages/LoanStructure/Logic'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { loanTypeMap } from 'reducers/loanDetail.reducer'
import { updateLoanFields } from 'services'
import { combinedMonthlyExpense } from 'services/pdfs/utils'
import { Input, Select } from 'stories/components'
import { formatDate, getLoanTerm, getPrice2decimal, getPrice3decimal, removeComma } from 'utils'
import { getInterestOrNot } from 'utils/getInterestOrNot'

import { AllowCashOutType, AllowCashOutTypeText } from './types'

export const LoanDetailsSlide = () => {
  const [isShowSlide, setShowSlide] = useState(false)
  const dispatch = useDispatch()

  const { hasPermission } = usePermissions()

  const canEditLoanDetailsSideBar = hasPermission('CAN_EDIT_LOAN_DETAILS_SIDE_BAR')

  const { borrower, loan, loanDetail } = useSelector((state: any) => ({
    borrower: state.borrower,
    loan: state.loan as ILoan,
    loanDetail: state.loanDetail,
  }))

  let assetTotal = 0
  borrower.borrower?.assets?.map((item: any) => {
    assetTotal += removeComma(item.balance)
  })

  const data = useMemo(() => {
    const pitis = combinedMonthlyExpense(loan, loanDetail.rateData, true)
    const ltvs = calculateLoanLimits(loan)
    const { value: cashToCloseBorrower, label: cashToCloseBorrowerLabel } = estimatedClosingAmountBorrower(
      loan,
      loanDetail.loanGlobalSettings,
    )
    const isRefinance = loan.transactionType === 'Refinance'
    const isCashFromBorrower = cashToCloseBorrowerLabel.includes('from')
    const isNonDSCR = isNonDSCRProduct(loan.productType)
    const isBridge = loan.productType === 'Bridge'
    let ltvObjs: any = {
      LTV: `${calculateLTV()} %`,
      CLTV: `${calculateLTV(null, true)} %`,
      'ARV LTV': `${ltvs.arv_ltv} %`,
      LTC: `${ltvs.ltc} %`,
      LTP: `${ltvs.ltp} %`,
    }
    if (loan.productType == ProductTypes.DSCR) {
      delete ltvObjs['ARV LTV']
      delete ltvObjs.LTC
      delete ltvObjs.LTP
    }
    if (loan.productType == ProductTypes.Bridge) {
      delete ltvObjs['ARV LTV']
    }
    if (isRefinance) {
      delete ltvObjs.LTP
    }

    const maxCash = removeComma(assetTotal) - removeComma(pitis['requiredReserves'])
    let maxCashLabel = `$ ${getPrice2decimal(maxCash, false, true)}`
    if (maxCash < 0) maxCashLabel = `$ 0.00`
    const excessCash = Math.abs(cashToCloseBorrower) - removeComma(pitis['requiredReserves'])
    let excessCashLabel = `$ ${getPrice2decimal(excessCash, false, true)}`
    if (excessCash < 0) excessCashLabel = `$ 0.00`
    const totalRequiredAssets =
      removeComma(pitis['requiredReserves']) + (isCashFromBorrower ? Math.abs(cashToCloseBorrower) : 0)
    let totalRequiredAssetsLabel = `$ ${getPrice2decimal(totalRequiredAssets, false, true)}`
    if (totalRequiredAssets < 0) totalRequiredAssetsLabel = `$ 0.00`

    let rlt: any = {
      'Borrower Info': {
        'First Name': borrower.borrower.firstName,
        'Last Name': borrower.borrower.lastName,
        'Entity Name': borrower.borrower.hasEntityTitle ? borrower.borrower.entityTitle : 'N/A',
        'Social Security Number': borrower.borrower.ssn,
        'Date of Birth': formatDate(borrower.borrower.dob),
        'Credit Score': loan.estimatedCreditScore,
        'Non-Person Entity': borrower.borrower.hasEntityTitle ? 'Yes' : 'No',
        Street: borrower.borrower.presentAddress,
        Citizenship: loan.residency,
        'First Time Home Buyer': loan.firstTimeHomeBuyer ? 'Yes' : 'No',
        'Marital Status': borrower.borrower.maritalStatus,
        'Work Phone': borrower.borrower.phone,
        Email: borrower.borrower.email,
      },
      'Property Info': {
        'Property Address': loan.subjectPropertyAddress,
        'Property Type': loan.propertyType,
        'Number of Units': loan.numberOfUnits,
      },
      'Loan Property Info': {
        'Occupancy Type': loan.proposedOccupancy,
        'Loan Purpose': loan.transactionType,
        'Loan Program Name': loanDetail.rateData?.program,
        // 'Mortgage Type': loan.mortgageLates,
        'Amortization Type': loan.amortizationType,
        'Loan Term': `${getLoanTerm(loanDetail.rateData?.type, loan.productType, loan.loanTerm)} months`,
        'Interest Rate': loanDetail.rateData?.rate,
        'Interest Only': getInterestOrNot(loanDetail.rateData?.type || loan.amortizationType) ? 'Yes' : 'No',
      },
      'Loan Details Info': {
        'Product Type': productTypeOptionsMap[loan.productType] || '',
        'Purchase Price': `$ ${getPrice3decimal(loan.propertyPurchasePrice)}`,
        ...(isNonDSCR
          ? {
              'Interest Reserve': `$ ${getPrice3decimal(loan.interestReserve)}`,
              ...(!isBridge
                ? {
                    'Construction Budget': `$ ${getPrice3decimal(loan.rehabBudget)}`,
                    'Construction Amount Financed': `$ ${getPrice3decimal(loan.constructionReserve)}`,
                  }
                : {}),
            }
          : {}),
        'Base Loan': `$ ${getPrice3decimal(loan.proposedLoanAmount)}`,
        'Loan With': `$ ${getPrice3decimal(loan.proposedLoanAmount)}`,
        [pitis['title']]: `$ ${getPrice3decimal(pitis['mortgagePayment'])}`,
        ...ltvObjs,
        DSCR: pitis['dscr'],
        [cashToCloseBorrowerLabel]: `$ ${getPrice3decimal(Math.abs(cashToCloseBorrower))}`,
        'Required Reserves': `$ ${getPrice2decimal(pitis['requiredReserves'], false, true)}`,
        'Total Required Assets': totalRequiredAssetsLabel,
        Assets: `$ ${getPrice3decimal(assetTotal)}`,
        'Max Allowable Cash to Close': maxCashLabel,
        ...(isRefinance ? { 'Cash to Borrower in Excess of reserve requirement': excessCashLabel } : {}),
        'Allow Cash-Out to be used as reserves': 'ALLOW_CASHOUT',
        ...([AllowCashOutType.YES_WITH_REQUIREMENT, AllowCashOutType.YES_WITH_RESTRICTION].includes(
          loan.allowCashOut?.toString() as AllowCashOutType,
        )
          ? { Percentage: 'PERCENTAGE' }
          : {}),
      },
      'Other Loan Info': {
        'Origination Channel': loanTypeMap[loanDetail.loanType],
        'Escrow Waiver': isNonDSCR ? 'Waive Both' : loan.escrowType,
        '1st Time Home Buyer': loan.firstTimeHomeBuyer ? 'Yes' : 'No',
        'Lock Expiration': loanDetail.rateData?.expireDate,
      },
    }

    if (
      fieldsByTransactionTypeAndProductTypeConstant[loan.productType]?.[loan.transactionType]?.indexOf(
        'propertyPurchasePrice',
      ) === -1
    ) {
      delete rlt['Loan Details Info']['Purchase Price']
    }

    if (ltvs.arv_ltv === undefined) delete rlt['Loan Details Info']['ARV LTV']
    if (ltvs.ltc === undefined) delete rlt['Loan Details Info']['LTC']
    if (ltvs.ltp === undefined) delete rlt['Loan Details Info']['LTP']

    return rlt
  }, [borrower, loan, loanDetail])

  const onShowSlide = () => {
    setShowSlide(!isShowSlide)
  }

  const onFieldChange = (key: string, value: any) => {
    if (key === 'allowCashOutPercentage') {
      value = Number(value)
      if (value < 0 || value > 100) return
    }
    const json: Record<string, any> = { [key]: value }
    if (key === 'allowCashOut') json['allowCashOutPercentage'] = 0
    updateLoanFields(json).then(() => {
      dispatch(setLoanGroupData(json))
    })
  }

  return (
    <>
      <div className="fixed left-0 top-[20%] z-40">
        <button
          className="p-3 rounded-l-none ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 border-l-0 w-[155px]"
          onClick={onShowSlide}
        >
          <DocumentTextIcon className="w-5 h-5" /> Loan Details
        </button>
      </div>
      {isShowSlide && (
        <div
          className={`z-50 shadow1 fixed bg-white border rounded-l left-0 top-[5vh] bottom-0 transition-[left] w-[24rem] h-[90vh] overflow-auto ease-in-out ${
            isShowSlide ? 'left-0' : '-left-[24rem]'
          }`}
        >
          <div className="w-[24rem] fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white">
            <div className="flex items-center">
              <p className="text-lg text-gray-800">Loan Details: </p>
              <span className="text-[16px] ml-2 italic font-semibold">
                {loanDetail.byteproFileName > 0 ? loanDetail.byteproFileName : loanDetail.loanNumber}
              </span>
            </div>
            <button className="hover:underline hover-shadow1 p-1 rounded" onClick={onShowSlide}>
              <ArrowLeftCircleIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="pt-14 h-fit overflow-auto relative text-sm">
            {Object.keys(data).map((groupName: string, index: number) => {
              const group = (data as any)[groupName]
              return (
                <div key={index}>
                  <p className="text-shade-blue bg-blue-200 py-1 px-3 border-b font-bold">- {groupName}</p>
                  <table className="w-full">
                    <tbody>
                      {Object.keys(group).map((key, index: number) => (
                        <React.Fragment key={index}>
                          <tr className={`${index % 2 && 'bg-gray-50'}`}>
                            <td className="py-1 px-2 border-b">{key}:</td>
                            <td className="font-variation-settings-600 border-b text-left py-1 pr-2">
                              {group[key] === 'ALLOW_CASHOUT' ? (
                                <div className="w-full [&>*:first-child]:mb-0">
                                  {canEditLoanDetailsSideBar ? (
                                    <Select
                                      id="cash-out-reserves"
                                      title=""
                                      options={AllowCashOutTypeText}
                                      value={loan.allowCashOut.toString()}
                                      onChange={(value) => onFieldChange('allowCashOut', value)}
                                      className="mb-0"
                                    />
                                  ) : (
                                    <span>{AllowCashOutTypeText[loan.allowCashOut]}</span>
                                  )}
                                </div>
                              ) : group[key] === 'PERCENTAGE' ? (
                                <div className="flex items-center gap-2">
                                  <div className="[&>*:first-child]:mb-0">
                                    {canEditLoanDetailsSideBar ? (
                                      <Input
                                        name="cash-out-percentage"
                                        title=""
                                        type="number"
                                        value={loan.allowCashOutPercentage}
                                        onChange={(value) => onFieldChange('allowCashOutPercentage', value)}
                                        className="w-24"
                                        placeholder="Enter %"
                                      />
                                    ) : (
                                      <span>{loan.allowCashOutPercentage}</span>
                                    )}
                                  </div>
                                  <span className="text-sm text-gray-600">%</span>
                                </div>
                              ) : (
                                <div dangerouslySetInnerHTML={{ __html: group[key] }} />
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
